/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { ProductTypeEnum } from './ProductTypeEnum';
import {
    ProductTypeEnumFromJSON,
    ProductTypeEnumFromJSONTyped,
    ProductTypeEnumToJSON,
} from './ProductTypeEnum';
import type { TokenContractInfoRequest } from './TokenContractInfoRequest';
import {
    TokenContractInfoRequestFromJSON,
    TokenContractInfoRequestFromJSONTyped,
    TokenContractInfoRequestToJSON,
} from './TokenContractInfoRequest';
import type { IssuerRequest } from './IssuerRequest';
import {
    IssuerRequestFromJSON,
    IssuerRequestFromJSONTyped,
    IssuerRequestToJSON,
} from './IssuerRequest';
import type { ProductValidationStatusEnum } from './ProductValidationStatusEnum';
import {
    ProductValidationStatusEnumFromJSON,
    ProductValidationStatusEnumFromJSONTyped,
    ProductValidationStatusEnumToJSON,
} from './ProductValidationStatusEnum';
import type { MoneyRequest } from './MoneyRequest';
import {
    MoneyRequestFromJSON,
    MoneyRequestFromJSONTyped,
    MoneyRequestToJSON,
} from './MoneyRequest';
import type { NetworkNameTechnicalEnum } from './NetworkNameTechnicalEnum';
import {
    NetworkNameTechnicalEnumFromJSON,
    NetworkNameTechnicalEnumFromJSONTyped,
    NetworkNameTechnicalEnumToJSON,
} from './NetworkNameTechnicalEnum';
import type { DocumentRequest } from './DocumentRequest';
import {
    DocumentRequestFromJSON,
    DocumentRequestFromJSONTyped,
    DocumentRequestToJSON,
} from './DocumentRequest';
import type { ProductStatusEnum } from './ProductStatusEnum';
import {
    ProductStatusEnumFromJSON,
    ProductStatusEnumFromJSONTyped,
    ProductStatusEnumToJSON,
} from './ProductStatusEnum';

/**
 * 
 * @export
 * @interface PatchedAdminProductDetailRequest
 */
export interface PatchedAdminProductDetailRequest {
    /**
     * 
     * @type {string}
     * @memberof PatchedAdminProductDetailRequest
     */
    agioPercent?: string;
    /**
     * 
     * @type {DocumentRequest}
     * @memberof PatchedAdminProductDetailRequest
     */
    backToBack?: DocumentRequest;
    /**
     * 
     * @type {string}
     * @memberof PatchedAdminProductDetailRequest
     */
    backupWallet?: string;
    /**
     * Defines whether commitment requires a bank account for investor
     * @type {boolean}
     * @memberof PatchedAdminProductDetailRequest
     */
    bankAccountRequired?: boolean;
    /**
     * Shall accrued interests be calculated for investments and shown in payment details?
     * @type {boolean}
     * @memberof PatchedAdminProductDetailRequest
     */
    calculateAccruedInterest?: boolean;
    /**
     * How many pieces of the digital representation equal one real-world asset.
     * @type {string}
     * @memberof PatchedAdminProductDetailRequest
     */
    calculationFactor?: string | null;
    /**
     * Deadline for the investor to be able to cancel the investment (in days) after signing
     * @type {number}
     * @memberof PatchedAdminProductDetailRequest
     */
    cancellationDeadline?: number;
    /**
     * 
     * @type {string}
     * @memberof PatchedAdminProductDetailRequest
     */
    code?: string;
    /**
     * This is a product with an eWpG issuance. That means we need to create registry statements
     * @type {boolean}
     * @memberof PatchedAdminProductDetailRequest
     */
    ewpg?: boolean;
    /**
     * Deadline for the investor making the bank transfer (in days)
     * @type {number}
     * @memberof PatchedAdminProductDetailRequest
     */
    investmentPaymentDeadline?: number;
    /**
     * Defines whether this token complies with ECSP.
     * @type {boolean}
     * @memberof PatchedAdminProductDetailRequest
     */
    isEcsp?: boolean;
    /**
     * ISIN of security
     * @type {string}
     * @memberof PatchedAdminProductDetailRequest
     */
    isin?: string | null;
    /**
     * 
     * @type {MoneyRequest}
     * @memberof PatchedAdminProductDetailRequest
     */
    issuancePrice?: MoneyRequest | null;
    /**
     * 
     * @type {IssuerRequest}
     * @memberof PatchedAdminProductDetailRequest
     */
    issuer?: IssuerRequest;
    /**
     * 
     * @type {string}
     * @memberof PatchedAdminProductDetailRequest
     */
    issuerWalletAddress?: string;
    /**
     * Lock-in time of the newly created contract. This information is needed at deployment time in cases where the logic is implemented in a smart contract (e.g. Ethereum), but in any case we need a place to store it before TokenContract exists.
     * @type {Date}
     * @memberof PatchedAdminProductDetailRequest
     */
    lockInTime?: Date | null;
    /**
     * 
     * @type {number}
     * @memberof PatchedAdminProductDetailRequest
     */
    maxIssuableAmount?: number;
    /**
     * 
     * @type {string}
     * @memberof PatchedAdminProductDetailRequest
     */
    name?: string;
    /**
     * 
     * @type {NetworkNameTechnicalEnum}
     * @memberof PatchedAdminProductDetailRequest
     */
    network?: NetworkNameTechnicalEnum;
    /**
     * 
     * @type {MoneyRequest}
     * @memberof PatchedAdminProductDetailRequest
     */
    nominalValue?: MoneyRequest | null;
    /**
     * Deadline for the investor making the bank transfer (in days)
     * @type {number}
     * @memberof PatchedAdminProductDetailRequest
     */
    paymentDeadline?: number;
    /**
     * Type of financial product
     * @type {ProductTypeEnum}
     * @memberof PatchedAdminProductDetailRequest
     */
    productType?: ProductTypeEnum;
    /**
     * 
     * @type {boolean}
     * @memberof PatchedAdminProductDetailRequest
     */
    provideNominalValue?: boolean;
    /**
     * A restricted publication is not shown on the public publications page.
     * @type {boolean}
     * @memberof PatchedAdminProductDetailRequest
     */
    publicationRestricted?: boolean;
    /**
     * Defines whether commitment requires a securities depot for investor
     * @type {boolean}
     * @memberof PatchedAdminProductDetailRequest
     */
    securitiesDepositAccountRequired?: boolean;
    /**
     * 
     * @type {ProductStatusEnum}
     * @memberof PatchedAdminProductDetailRequest
     */
    status?: ProductStatusEnum;
    /**
     * step size for units: investors must enter an integer multiple of the step size
     * @type {number}
     * @memberof PatchedAdminProductDetailRequest
     */
    stepSize?: number;
    /**
     * 
     * @type {TokenContractInfoRequest}
     * @memberof PatchedAdminProductDetailRequest
     */
    tokenizationInfo?: TokenContractInfoRequest | null;
    /**
     * 
     * @type {string}
     * @memberof PatchedAdminProductDetailRequest
     */
    validationRejectionReason?: string;
    /**
     * Crypto securities must be validated by Cashlink before they are allowed to be distributed. validation_status describes the progress step of the validation in which the product currently is. Manual validation by Cashlink occurs while a crypto security is in step 'VALIDATION_PENDING'
     * @type {ProductValidationStatusEnum}
     * @memberof PatchedAdminProductDetailRequest
     */
    validationStatus?: ProductValidationStatusEnum | null;
    /**
     * 
     * @type {DocumentRequest}
     * @memberof PatchedAdminProductDetailRequest
     */
    vib?: DocumentRequest;
    /**
     * URL to the product's website
     * @type {string}
     * @memberof PatchedAdminProductDetailRequest
     */
    website?: string | null;
}



/**
 * Check if a given object implements the PatchedAdminProductDetailRequest interface.
 */
export function instanceOfPatchedAdminProductDetailRequest(value: object): value is PatchedAdminProductDetailRequest {
    return true;
}

export function PatchedAdminProductDetailRequestFromJSON(json: any): PatchedAdminProductDetailRequest {
    return PatchedAdminProductDetailRequestFromJSONTyped(json, false);
}

export function PatchedAdminProductDetailRequestFromJSONTyped(json: any, ignoreDiscriminator: boolean): PatchedAdminProductDetailRequest {
    if (json == null) {
        return json;
    }
    return {
        
        'agioPercent': json['agio_percent'] == null ? undefined : json['agio_percent'],
        'backToBack': json['back_to_back'] == null ? undefined : DocumentRequestFromJSON(json['back_to_back']),
        'backupWallet': json['backup_wallet'] == null ? undefined : json['backup_wallet'],
        'bankAccountRequired': json['bank_account_required'] == null ? undefined : json['bank_account_required'],
        'calculateAccruedInterest': json['calculate_accrued_interest'] == null ? undefined : json['calculate_accrued_interest'],
        'calculationFactor': json['calculation_factor'] == null ? undefined : json['calculation_factor'],
        'cancellationDeadline': json['cancellation_deadline'] == null ? undefined : json['cancellation_deadline'],
        'code': json['code'] == null ? undefined : json['code'],
        'ewpg': json['ewpg'] == null ? undefined : json['ewpg'],
        'investmentPaymentDeadline': json['investment_payment_deadline'] == null ? undefined : json['investment_payment_deadline'],
        'isEcsp': json['is_ecsp'] == null ? undefined : json['is_ecsp'],
        'isin': json['isin'] == null ? undefined : json['isin'],
        'issuancePrice': json['issuance_price'] == null ? undefined : MoneyRequestFromJSON(json['issuance_price']),
        'issuer': json['issuer'] == null ? undefined : IssuerRequestFromJSON(json['issuer']),
        'issuerWalletAddress': json['issuer_wallet_address'] == null ? undefined : json['issuer_wallet_address'],
        'lockInTime': json['lock_in_time'] == null ? undefined : (new Date(json['lock_in_time'])),
        'maxIssuableAmount': json['max_issuable_amount'] == null ? undefined : json['max_issuable_amount'],
        'name': json['name'] == null ? undefined : json['name'],
        'network': json['network'] == null ? undefined : NetworkNameTechnicalEnumFromJSON(json['network']),
        'nominalValue': json['nominal_value'] == null ? undefined : MoneyRequestFromJSON(json['nominal_value']),
        'paymentDeadline': json['payment_deadline'] == null ? undefined : json['payment_deadline'],
        'productType': json['product_type'] == null ? undefined : ProductTypeEnumFromJSON(json['product_type']),
        'provideNominalValue': json['provide_nominal_value'] == null ? undefined : json['provide_nominal_value'],
        'publicationRestricted': json['publication_restricted'] == null ? undefined : json['publication_restricted'],
        'securitiesDepositAccountRequired': json['securities_deposit_account_required'] == null ? undefined : json['securities_deposit_account_required'],
        'status': json['status'] == null ? undefined : ProductStatusEnumFromJSON(json['status']),
        'stepSize': json['step_size'] == null ? undefined : json['step_size'],
        'tokenizationInfo': json['tokenization_info'] == null ? undefined : TokenContractInfoRequestFromJSON(json['tokenization_info']),
        'validationRejectionReason': json['validation_rejection_reason'] == null ? undefined : json['validation_rejection_reason'],
        'validationStatus': json['validation_status'] == null ? undefined : ProductValidationStatusEnumFromJSON(json['validation_status']),
        'vib': json['vib'] == null ? undefined : DocumentRequestFromJSON(json['vib']),
        'website': json['website'] == null ? undefined : json['website'],
    };
}

export function PatchedAdminProductDetailRequestToJSON(value?: PatchedAdminProductDetailRequest | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'agio_percent': value['agioPercent'],
        'back_to_back': DocumentRequestToJSON(value['backToBack']),
        'backup_wallet': value['backupWallet'],
        'bank_account_required': value['bankAccountRequired'],
        'calculate_accrued_interest': value['calculateAccruedInterest'],
        'calculation_factor': value['calculationFactor'],
        'cancellation_deadline': value['cancellationDeadline'],
        'code': value['code'],
        'ewpg': value['ewpg'],
        'investment_payment_deadline': value['investmentPaymentDeadline'],
        'is_ecsp': value['isEcsp'],
        'isin': value['isin'],
        'issuance_price': MoneyRequestToJSON(value['issuancePrice']),
        'issuer': IssuerRequestToJSON(value['issuer']),
        'issuer_wallet_address': value['issuerWalletAddress'],
        'lock_in_time': value['lockInTime'] == null ? undefined : ((value['lockInTime'] as any).toISOString()),
        'max_issuable_amount': value['maxIssuableAmount'],
        'name': value['name'],
        'network': NetworkNameTechnicalEnumToJSON(value['network']),
        'nominal_value': MoneyRequestToJSON(value['nominalValue']),
        'payment_deadline': value['paymentDeadline'],
        'product_type': ProductTypeEnumToJSON(value['productType']),
        'provide_nominal_value': value['provideNominalValue'],
        'publication_restricted': value['publicationRestricted'],
        'securities_deposit_account_required': value['securitiesDepositAccountRequired'],
        'status': ProductStatusEnumToJSON(value['status']),
        'step_size': value['stepSize'],
        'tokenization_info': TokenContractInfoRequestToJSON(value['tokenizationInfo']),
        'validation_rejection_reason': value['validationRejectionReason'],
        'validation_status': ProductValidationStatusEnumToJSON(value['validationStatus']),
        'vib': DocumentRequestToJSON(value['vib']),
        'website': value['website'],
    };
}

